/**
 * Gate for speech-to-text results. An open mic turns background noise into
 * short garbled "utterances" in random languages ("Es de eine", "Papas."); every
 * one of them used to reach the AI, cost credits and get an answer. This is the
 * single decision point both voice send paths (Speak pill, Ctrl push-to-talk)
 * ask before sending.
 *
 * Deliberately conservative: a real terse command ("news", "AAPL", "$TSLA")
 * must pass, so only evidence-backed signals drop a turn.
 */

export type TranscriptDropReason =
  | 'empty'
  | 'too_short'
  | 'filler'
  | 'language_mismatch'
  | 'low_confidence'

export type TranscriptAssessment = { ok: true } | { ok: false; reason: TranscriptDropReason }

export interface TranscriptInput {
  text: string
  /** Language the STT provider reported for the utterance (e.g. 'en', 'es-ES'). */
  detectedLanguage?: string | null
  /** STT confidence 0-1. Absent for providers that do not report it. */
  confidence?: number | null
  /** The app's chosen companion language (e.g. 'en'). */
  expectedLanguage?: string | null
}

/** Below this the recognizer itself is guessing. */
export const MIN_TRANSCRIPT_CONFIDENCE = 0.6

/**
 * Words that, alone, are acknowledgements or noise rather than a request.
 * Lowercase; compared after stripping punctuation.
 */
const FILLER_WORDS = new Set([
  // English
  'ah',
  'uh',
  'um',
  'umm',
  'uhh',
  'hmm',
  'hm',
  'mm',
  'mhm',
  'eh',
  'oh',
  'e',
  'o',
  'a',
  'but',
  'so',
  'sure',
  'yeah',
  'yes',
  'yep',
  'ok',
  'okay',
  'hold',
  'well',
  // Other languages
  'da',
  'si',
  'sí',
  'ja',
  'nein',
  'oui',
  'ben',
  'bueno',
  'pues',
  'äh',
  'ähm',
  'tja',
  'nu',
  'haan',
  'हाँ',
  'हां',
  'ठीक',
])

const LETTER = /\p{L}/gu
const TICKER_CASHTAG = /^\$[A-Za-z]{1,5}$/
const TICKER_CAPS = /^[A-Z]{2,5}$/

function normalizeWord(word: string): string {
  return word.toLowerCase().replace(/^[^\p{L}\p{M}\p{N}$]+|[^\p{L}\p{M}\p{N}]+$/gu, '')
}

/** $TSLA, or 2-5 capitals that are not a filler ("OK", "SO" stay filler). */
function isTickerLike(rawWord: string): boolean {
  const word = rawWord.replace(/^[^\p{L}\p{M}\p{N}$]+|[^\p{L}\p{M}\p{N}]+$/gu, '')
  if (TICKER_CASHTAG.test(word)) return true
  return TICKER_CAPS.test(word) && !FILLER_WORDS.has(word.toLowerCase())
}

function primarySubtag(lang: string): string {
  return lang.trim().toLowerCase().split(/[-_]/)[0] ?? ''
}

export function assessTranscript(input: TranscriptInput): TranscriptAssessment {
  const text = input.text.trim()
  if (!text) return { ok: false, reason: 'empty' }

  const letters = text.match(LETTER)?.length ?? 0
  if (letters < 2) return { ok: false, reason: 'too_short' }

  const words = text.split(/\s+/).filter(Boolean)
  const hasTicker = words.some(isTickerLike)
  if (!hasTicker && words.every((w) => FILLER_WORDS.has(normalizeWord(w)))) {
    return { ok: false, reason: 'filler' }
  }

  // Tickers are language-neutral: "AAPL" from a Spanish-app user often comes
  // back detected as English, and is still a real question.
  const onlyTickers = words.every(isTickerLike)
  if (!onlyTickers && input.detectedLanguage && input.expectedLanguage) {
    if (primarySubtag(input.detectedLanguage) !== primarySubtag(input.expectedLanguage)) {
      return { ok: false, reason: 'language_mismatch' }
    }
  }

  if (typeof input.confidence === 'number' && input.confidence < MIN_TRANSCRIPT_CONFIDENCE) {
    return { ok: false, reason: 'low_confidence' }
  }

  return { ok: true }
}
